import {
  Box,
  debounce,
  Dialog,
  PopoverActions,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'

import Icon from '@src/components/shared/Icon'

import { CalendarEventType } from './Calendar'
import reactBigCalendarLinkHack from './reactBigCalendarLinkHack'
import ShareCard from './ShareCard'

const useStyles = makeStyles()((theme) => ({
  eventText: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.common.white,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  },
  campaignStart: {
    background: theme.palette.success.main,
  },
  campaignEnd: {
    background: theme.palette.error.main,
  },
  automation: {
    background: '#3f51b5',
  },
  scheduled_share: {
    background: '#ffc208',
  },
  executed_share: {
    background: '#9b26af',
  },
}))
interface CalendarEventProps {
  event: CalendarEventType
  onClose?: () => void
}

const CalendarEvent: React.FC<React.PropsWithChildren<CalendarEventProps>> = ({
  event: calendarEvent,
  onClose,
}) => {
  const { classes, cx } = useStyles()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [activeEvent, setActiveEvent] = useState<CalendarEventType | null>(null)

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    calendarEvent: CalendarEventType,
  ) => {
    setAnchorEl(event.currentTarget)
    setActiveEvent(calendarEvent)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  const popoverActionRef = useRef<PopoverActions | null>(null)
  const popoverContentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Normally you would just call `window.dispatchEvent(new Event('resize'))`
    // to adjust ANY popup in mui.
    // react-big-calendar listens to that event and re-renders, which causes the
    // popup to disappear.
    let element: typeof popoverContentRef.current = null

    const debouncedUpdatePosition = debounce(() => {
      popoverActionRef.current?.updatePosition()
    })

    const observer = new ResizeObserver(() => {
      debouncedUpdatePosition()
    })

    // popoverContentRef isn't set exactly when `open` changes (react portal
    // quirks?), so we need to wait.
    requestAnimationFrame(() => {
      element = popoverContentRef.current

      if (open && element) {
        observer.observe(element)
      }
    })

    return () => {
      if (open && element) {
        observer.unobserve(element)
      }
    }
  }, [open])

  return (
    <div>
      <Box
        display="flex"
        flexWrap="nowrap"
        paddingX={1}
        paddingY={0.25}
        mb={0.5}
        className={cx(
          classes.eventText,
          calendarEvent.type === 'campaign'
            ? calendarEvent.isStart
              ? classes.campaignStart
              : classes.campaignEnd
            : classes[calendarEvent.type],
        )}
        borderRadius="4px"
        onClick={(e) => {
          anchorEl ? handlePopoverClose() : handlePopoverOpen(e, calendarEvent)
        }}
      >
        {calendarEvent.channel && (
          <Box
            marginRight=".5rem"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Icon
              color={'white'}
              size={15}
              icon={
                calendarEvent.channel === 'email_shares'
                  ? 'email_open'
                  : calendarEvent.channel.replace('_shares', '')
              }
            />
          </Box>
        )}
        <EventText calendarEvent={calendarEvent} />
      </Box>
      <Dialog
        open={open}
        onClose={handlePopoverClose}
        disableRestoreFocus
        TransitionProps={{
          onExited: () => {
            setActiveEvent(null)
            onClose?.()
          },
        }}
        fullWidth
      >
        <div ref={popoverContentRef} onClick={reactBigCalendarLinkHack}>
          {activeEvent &&
            ['executed_share', 'scheduled_share'].includes(
              activeEvent.type,
            ) && <ShareCard event={activeEvent} onClose={handlePopoverClose} />}
        </div>
      </Dialog>
    </div>
  )
}

interface EventTextProps {
  calendarEvent: CalendarEventType
}
const EventText: React.FC<React.PropsWithChildren<EventTextProps>> = ({
  calendarEvent,
}) => {
  return (
    <Typography noWrap variant="caption">
      {calendarEvent.title}
    </Typography>
  )
}

export default CalendarEvent
